/**
 * Master enum of every telemetry event name emitted across the weavix monorepo.
 *
 * ## Adding a new event
 *
 * 1. Add a member to this enum.
 * 2. Run `yarn generate` from `packages/telemetry`.
 *    - `generate-event-stubs.cjs` creates `src/events/<kebab-name>.ts` (stub).
 *    - `generate-index.cjs` updates `src/telemetry-event-payloads.ts` and `src/index.ts`.
 * 3. Open the new stub in `src/events/` and fill in the payload fields by
 *    inspecting the call site(s) where the event is emitted. Document the
 *    interface and every property: the event dictionary published for the
 *    platform is generated from these comments.
 * 4. Emit it — `sendTelemetry` from client code, `sendTelemetryEvent` from
 *    server-side code. Device, user, account and app attributes are added by
 *    the sender, so a payload carries only its own fields.
 */
export enum TelemetryEvent {
    // --- PTT ---
    /** PTT audio message was sent by the local device. */
    PttSent = 'PttSent',
    /** Playback jitter detected on a received PTT (buffer underrun / lag). */
    PttJitter = 'PttJitter',
    /** Packet loss / jitter detected on a sent PTT. */
    PttSentJitter = 'PttSentJitter',
    /** Incoming PTT was skipped without playing (muted, not in facility, etc.). */
    PttSkipped = 'PttSkipped',
    /** Active PTT channel update was not pushed to the OS because the session is suspended. */
    PttChannelUpdateSkipped = 'PttChannelUpdateSkipped',
    /** Outgoing PTT attempt was denied because the channel was already occupied. */
    PttBuzzed = 'PttBuzzed',
    /** PTT hardware button was physically pressed. */
    PttPressed = 'PttPressed',
    /** PTT audio message was received and played back. */
    PttReceive = 'PttReceive',
    /** PTT playback was interrupted by a higher-priority audio event. */
    PttInterrupted = 'PttInterrupted',
    /** Held PTT key recovered after the channel cleared (denial resolved). */
    PttDenialRecovered = 'PttDenialRecovered',
    /** A press the user made that never transmitted: the key was released while the channel was still occupied, or the press was cancelled before the microphone opened. See `reason`. */
    PttDenialReleased = 'PttDenialReleased',
    /** Lifecycle stage marker emitted at multiple points in the PTT send/receive flow. */
    PttTimeline = 'PttTimeline',
    /** Network quality metrics sampled during PTT (native Android). */
    PttNetworkTelemetry = 'PttNetworkTelemetry',
    /** Per-PTT packet summary emitted after playback ends on Walt (native Android). */
    PttPacketTelemetry = 'PttPacketTelemetry',
    /** Periodic packet-count summary per network type on Walt (native Android). */
    PttPacketCount = 'PttPacketCount',
    /** Per-PTT summary of a PTT a walt relayed between LoRa and DECT, in either direction (native Android). */
    PttLoraRelay = 'PttLoraRelay',
    /** Per-PTT outcome of a PTT transcript a walt received in-band over LoRa (native Android). */
    PttLoraTranscript = 'PttLoraTranscript',
    /** The account-level LoRa kill switch (FeatureId.DisableLora) as a walt applied it, on each change (walt). */
    LoraKillSwitchChanged = 'LoraKillSwitchChanged',

    // --- PTT micro-service (ptt-service pod ownership) ---
    /** A PTT was assigned to a ptt-service pod, which then owns its stream. Emitted once per PTT by the pod that acquired it. */
    PttStreamAssigned = 'PttStreamAssigned',
    /** A PTT's stream completed normally and its owning pod released it. */
    PttStreamCompleted = 'PttStreamCompleted',
    /** A ptt-service pod took over a PTT whose previous owner stopped renewing its lease. */
    PttStreamRecovered = 'PttStreamRecovered',
    /** A ptt-service pod lost its ownership lease mid-PTT and stood down. */
    PttStreamOwnershipLost = 'PttStreamOwnershipLost',
    /** A PTT was discarded by ptt-service without being processed. */
    PttStreamDropped = 'PttStreamDropped',
    /** A clip's scratch recording could not be read back, so its stream was destroyed. */
    RecordingStreamFailed = 'RecordingStreamFailed',
    /** A ptt-service pod finished draining its in-flight PTTs during shutdown. */
    PttServiceDrained = 'PttServiceDrained',
    /** ptt-service resolved a PTT's recipients and began forwarding its audio to them. */
    PttFanoutStarted = 'PttFanoutStarted',
    /** ptt-service could not forward a PTT that had listeners, so that audio was never heard. */
    PttFanoutFailed = 'PttFanoutFailed',
    /** ptt-service could not hand a PTT to the backend, so it produces no transcription or message. */
    PttHandoffFailed = 'PttHandoffFailed',
    /** A peer claimed a handoff a pod died holding and retried the pickup; the pickup may still fail. */
    PttHandoffRecovered = 'PttHandoffRecovered',
    /** A PTT's audio could not be converted to PCM, so it gets no transcript or translation. */
    PttPcmConversionFailed = 'PttPcmConversionFailed',

    // --- Speech / translation ---
    /** Speech was transcribed to text — a PTT channel transmission or walt dictation, per `surface`. */
    SpeechTranscriptionEvent = 'SpeechTranscriptionEvent',
    /** walt's on-device recognizer finished a mesh PTT without a usable transcript. */
    MeshTranscriptionDegraded = 'MeshTranscriptionDegraded',
    /** A dictation session never opened the mic — the start cue's completion never reached the guard that arms it. */
    SttCaptureSuppressed = 'SttCaptureSuppressed',
    /** Source language was detected via Azure Cognitive Services. */
    TextIdentificationEvent = 'TextIdentificationEvent',
    /** Text was translated via Azure Cognitive Services. */
    TextTranslationEvent = 'TextTranslationEvent',
    /** Server TTS could not look up a site's respellings, so it spoke the text as sent. */
    TextToSpeechPronunciationLookupFailed = 'TextToSpeechPronunciationLookupFailed',
    /** The on-device MADLAD model failed to translate a mesh transcript. */
    OnDeviceTranslationFailed = 'OnDeviceTranslationFailed',

    // --- Networking / MQTT ---
    /** A client connected via MQTT. */
    MqttConnect = 'MqttConnect',
    /** A client disconnected from MQTT. */
    MqttDisconnect = 'MqttDisconnect',
    /** Diagnostic snapshot of a single MQTT message. */
    MqttPacket = 'MqttPacket',
    /** EMQX provisioning finished with authorization settings unapplied, so the broker may still cache auth decisions. */
    EmqxProvisioningFailed = 'EmqxProvisioningFailed',
    /** Device detected a poor network signal. */
    PoorNetwork = 'PoorNetwork',
    /** Snapshot of all visible Wi-Fi access points and the connected network. */
    ObservedNetworks = 'ObservedNetworks',
    /** Device roamed to a different Wi-Fi access point. */
    WifiAccessPointChange = 'WifiAccessPointChange',
    /** Device changed LTE cell tower. */
    LteTowerChange = 'LteTowerChange',
    /** A preferred SIM failed to register on the cellular network with no registered SIM to fall back to. */
    StubbornSim = 'StubbornSim',
    /** Wi-Fi was disconnected and later reconnected; records the gap duration. */
    WifiNetworkDisconnectedDuration = 'WifiNetworkDisconnectedDuration',
    /** Mobile data was disconnected and later reconnected; records the gap duration. */
    MobileNetworkDisconnectedDuration = 'MobileNetworkDisconnectedDuration',
    /** Both Wi-Fi and mobile data were simultaneously disconnected; records the gap duration. */
    AllNetworksDisconnectedDuration = 'AllNetworksDisconnectedDuration',
    /** Known facility Wi-Fi networks were detected nearby for the first time. */
    FacilityNetworksFoundEvent = 'FacilityNetworksFoundEvent',
    /** The set of detected facility Wi-Fi networks changed. */
    FacilityNetworksUpdateEvent = 'FacilityNetworksUpdateEvent',

    // --- Location ---
    /** Location fix computed via the Combain Wi-Fi/cell positioning API. */
    CombainLocationTelemetry = 'CombainLocationTelemetry',
    /** Location fix computed via beacon trilateration. */
    TrilaterationTelemetry = 'TrilaterationTelemetry',
    /** A location fix was reported to the platform. */
    LocationData = 'LocationData',
    /** Periodic heartbeat confirming the location pipeline is alive. */
    LocationHeartbeat = 'LocationHeartbeat',
    /** The mobile LocationManager started producing fixes. */
    LocationManagerStart = 'LocationManagerStart',
    /** The mobile LocationManager stopped producing fixes. */
    LocationManagerStop = 'LocationManagerStop',
    /** The GPS hardware provider started. */
    GpsStart = 'GpsStart',
    /** The GPS hardware provider stopped. */
    GpsStop = 'GpsStop',

    // --- Backend health / auth ---
    /** A deprecated high-load endpoint was called. */
    HeavyRequest = 'HeavyRequest',
    /** An abusive or deprecated endpoint was called. */
    EvilUserRequest = 'EvilUserRequest',
    /** A MongoDB query hit the configured result-count limit. */
    QueryLimitEvent = 'QueryLimitEvent',
    /** The Node.js event loop was blocked for too long. */
    ServerBlocked = 'ServerBlocked',
    /** The Hermes JS thread was blocked for too long. */
    HermesBlocked = 'HermesBlocked',
    /** Node.js event-loop block with a profiler stack trace. */
    ServerBlockedAt = 'ServerBlockedAt',
    /** Hermes JS-thread block with a profiler stack trace. */
    HermesBlockedAt = 'HermesBlockedAt',
    /** Mobile/walt JS thread blocks. May not include very short blocks. */
    JavaBlockedAt = 'JavaBlockedAt',
    /** Web client JS thread block detected. */
    ClientJsBlocked = 'ClientJsBlocked',
    /** A request was authenticated with an API key. */
    AccountApiKeyUsed = 'AccountApiKeyUsed',

    // --- App lifecycle ---
    /** App moved to the background. */
    AppBackgrounded = 'AppBackgrounded',
    /** App returned to the foreground. */
    AppForegrounded = 'AppForegrounded',
    /** App restarted unexpectedly (crash or OOM kill). */
    AppUnexpectedRestart = 'AppUnexpectedRestart',
    /** App restarted intentionally (e.g. after an update). */
    AppExpectedRestart = 'AppExpectedRestart',
    /** App launched cold (first start). */
    AppLaunch = 'AppLaunch',
    /** Periodic app-activity snapshot. */
    ActivityMonitor = 'ActivityMonitor',
    /** Periodic resource-usage snapshot (CPU, memory, network, battery). */
    ResourcesUsed = 'ResourcesUsed',
    /** Ambient noise level sampled while the user is on-site (Walt only). */
    AmbientNoiseSample = 'AmbientNoiseSample',
    /** Screen was kept awake longer than the expected maximum duration. */
    ScreenAwakeTooLong = 'ScreenAwakeTooLong',
    /** Lifecycle stage marker emitted throughout the mobile startup sequence. */
    MobileStartupTimeline = 'MobileStartupTimeline',

    // --- Auth / login ---
    /** Mobile app startup duration (misnamed, includes non-login resumes). */
    LoginTime = 'LoginTime',
    /** User completed a fresh login on mobile. */
    MobileLogin = 'MobileLogin',
    /** User logged out on mobile. */
    MobileLogout = 'MobileLogout',
    /** Backend refused a PIN reset code redeemed with a login that is not its owner's. */
    PinResetCodeRefused = 'PinResetCodeRefused',
    /** Terminal outcome of a local-login upgrade's post-install handoff on Walt. */
    WaltLocalLoginUpgradeCompletion = 'WaltLocalLoginUpgradeCompletion',
    /** Native invalidated a provisional session; excludes JavaScript-initiated logout. */
    WaltLocalLoginInvalidated = 'WaltLocalLoginInvalidated',

    // --- User / account provisioning ---
    /** A user accepted an account invite, including partial-state recovery outcomes. */
    AccountInviteAcceptance = 'AccountInviteAcceptance',
    /** A user was deleted, emitted per-account during cleanup and once overall. */
    UserDeletion = 'UserDeletion',
    /** A user's read-aloud voice was changed, by the user or an admin. */
    UserVoiceChanged = 'UserVoiceChanged',

    // --- Messaging / channels ---
    /** Client detected a gap in the channel message sequence. */
    MissingMessages = 'MissingMessages',
    /** Channel read-sequence drifted and was auto-corrected. */
    ChannelSelfCorrection = 'ChannelSelfCorrection',
    /** User added a channel to their favourites. */
    ChannelFavorited = 'ChannelFavorited',
    /** User removed a channel from their favourites. */
    ChannelUnfavorited = 'ChannelUnfavorited',
    /** User hid a channel from their list; it returns with its next message. */
    ChannelHidden = 'ChannelHidden',
    /** A speech-to-text chunk arrived after a timed-out PTT message and resolved it. */
    ChannelMessageFailureResolved = 'ChannelMessageFailureResolved',
    /** A client-created PTT never reached the backend and its bubble was settled as failed. */
    ChannelMessageSendFailed = 'ChannelMessageSendFailed',
    /** A PTT's bubble settled with its audio intact but no transcript. */
    ChannelMessageTranscriptionMissing = 'ChannelMessageTranscriptionMissing',
    /** A message was created and dispatched to channel subscribers. */
    ChannelMessageNotify = 'ChannelMessageNotify',
    /** Media attached to a message never left the device and the message was marked failed. */
    ChannelAttachmentSendFailed = 'ChannelAttachmentSendFailed',
    /** A walt could not read an incoming channel message aloud, or the message had nothing to speak. */
    ChannelMessageReadAloudFailed = 'ChannelMessageReadAloudFailed',
    /** A walt finished reading a translated PTT aloud; see `ChannelMessageReadAloudPayload` for what is sampled. */
    ChannelMessageReadAloud = 'ChannelMessageReadAloud',
    /** User's read position advanced, marking messages as read. */
    MessageRead = 'MessageRead',

    // --- Support / feedback ---
    /** Zendesk sent a reply or status-change webhook for a support ticket. */
    ZendeskReplyWebhook = 'ZendeskReplyWebhook',
    /** A new Zendesk support ticket was created. */
    SupportTicketCreated = 'SupportTicketCreated',

    // --- Weather alerts ---
    /** A weather-alert workflow trigger was created, updated, or deleted (adoption / config metrics). */
    WeatherAlertTriggerConfigured = 'WeatherAlertTriggerConfigured',
    /** A polled weather alert was matched and assigned to an account's facility (fire / ingestion-latency metrics). */
    WeatherAlertFacilityMatched = 'WeatherAlertFacilityMatched',
    /** A weather alert poll completed successfully (feed-availability metric). */
    WeatherAlertPollCompleted = 'WeatherAlertPollCompleted',
    /** Matching a poll's alerts against an account's facilities failed. */
    WeatherAlertFacilityMatchFailed = 'WeatherAlertFacilityMatchFailed',
    /** A condition was detected during a weather alert poll that may result in data loss. */
    WeatherAlertPollDataLossRisk = 'WeatherAlertPollDataLossRisk',
    /** A weather alert provider fetch failed. */
    WeatherAlertPollFetchFailed = 'WeatherAlertPollFetchFailed',
    /** The NWS weather alert poll has not run successfully within its SLA. */
    WeatherAlertPollSlaMissed = 'WeatherAlertPollSlaMissed',
    /** A NWS zone geometry changed since it was last cached. */
    WeatherAlertZoneGeometryChanged = 'WeatherAlertZoneGeometryChanged',
    /** Fetching geometry for a NWS zone URL failed. */
    WeatherAlertZoneGeometryFetchFailed = 'WeatherAlertZoneGeometryFetchFailed',
    /** A NWS zone URL has no geometry available. */
    WeatherAlertZoneGeometryMissing = 'WeatherAlertZoneGeometryMissing',

    // --- UX / banners / music ---
    /** An in-app banner was shown to the user. */
    BannerDisplayed = 'BannerDisplayed',
    /** User tapped an in-app banner. */
    BannerClicked = 'BannerClicked',
    /** User dismissed an in-app banner by swiping. */
    BannerSwipedAway = 'BannerSwipedAway',
    /** User selected a music station (Walt only). */
    MusicStationSelected = 'MusicStationSelected',
    /** Music playback was interrupted (PTT, call, etc.). */
    MusicInterrupted = 'MusicInterrupted',
    /** User picked a theme (Light, Dark or Match device) in the Weavix Desktop App. */
    DesktopThemeChanged = 'DesktopThemeChanged',

    // --- Integrations ---
    /** Microsoft Teams change-notification or validation event received. */
    TeamsSubscription = 'TeamsSubscription',

    // --- Sync ---
    /** Client connected and began the initial sync. */
    SyncStartup = 'SyncStartup',
    /** Summary of sync events deduplicated from simulcast. */
    SyncDeduplicate = 'SyncDeduplicate',
    /** Summary of missed sync events, either in sequence or from a network. */
    SyncEventMissing = 'SyncEventMissing',
    /** Per-minute walt assignment reads, split by whether the caller was recognised as that walt and so got its account's enabled actions. */
    WaltAssignmentRead = 'WaltAssignmentRead',
    /** The desired state of native sync changed (activated or deactivated, tracking who triggered it). */
    NativeSyncDesiredChange = 'NativeSyncDesiredChange',
    /** A native sync partition (Turbo Module) became active after completing its initial data fetch. */
    SyncPartitionActive = 'SyncPartitionActive',
    /** A caller was refused a sync topic belonging to another account or user. */
    SyncTopicAccessDenied = 'SyncTopicAccessDenied',

    // --- Web page views ---
    /** User navigated to a console (admin portal) page. */
    ConsolePageViewed = 'ConsolePageViewed',
    /** User navigated to a web-radio page. */
    WebRadioPageViewed = 'WebRadioPageViewed',

    // --- Site survey ---
    /** Legacy facility survey record submitted. */
    SiteSurvey = 'SiteSurvey',
    /** A site-survey reading was created. */
    SiteSurveyReadingCreated = 'SiteSurveyReadingCreated',
    /** A site-survey observation was submitted. */
    SiteSurveyObservationSubmitted = 'SiteSurveyObservationSubmitted',
    /** A Walt BT device connected to the survey app. */
    SiteSurveyWaltConnected = 'SiteSurveyWaltConnected',
    /** A Walt BT device disconnected unexpectedly during a survey. */
    SiteSurveyWaltLost = 'SiteSurveyWaltLost',
    /** A Walt was abandoned (survey session ended without completion). */
    SiteSurveyWaltAbandoned = 'SiteSurveyWaltsAbandoned',
    /** A Walt took a survey observation (network scan). */
    SiteSurveyWaltObservationTaken = 'SiteSurveyWaltObservationTaken',

    // --- Battery / audio / docks ---
    /** End-of-battery-session summary published (Walt only). */
    BatterySessionPublish = 'BatterySessionPublish',
    /** A new battery session started (Walt only). */
    BatterySessionStart = 'BatterySessionStart',
    /** A queued audio item began playback. */
    QueuedAudioDidPlay = 'QueuedAudioDidPlay',
    /** Walt was placed in a dock that reported bad charging status. */
    BadDock = 'BadDock',
    /** End-of-audio-session summary published (Walt only). */
    AudioSessionPublish = 'AudioSessionPublish',
    /** The client audio manager ignored a stop request, e.g. a repeat for an event already stopping. */
    AudioEventStopRejected = 'AudioEventStopRejected',
    /** A phone call started or ended on an Android phone; app audio is superseded while it lasts. */
    PhoneCallStateChanged = 'PhoneCallStateChanged',
    /** Non-PTT app audio (alert, page, TTS) was denied, or any audio interrupted, because of a phone call. */
    AudioSupersededByPhoneCall = 'AudioSupersededByPhoneCall',

    // --- Walt ---
    /** Walt is connected to a weaker AP while a stronger known AP is visible. */
    WaltBetterWifiDetected = 'WaltBetterWifiDetected',
    /** Walt minted its device token with its backup credential after the current one was missing or rejected. */
    WaltCredentialRecovered = 'WaltCredentialRecovered',
    /** Walt credential registration failed. */
    WaltCredentialRegistrationFailed = 'WaltCredentialRegistrationFailed',
    /** Walt credential update failed. */
    WaltCredentialUpdateFailed = 'WaltCredentialUpdateFailed',
    /** Walt credential validation failed (backend or device side). */
    WaltCredentialValidationFailed = 'WaltCredentialValidationFailed',
    /** Walt is not on Wi-Fi but a known network is visible and in range. */
    WaltMissingWifiDetected = 'WaltMissingWifiDetected',
    /** PTT recording produced zero audio bytes. */
    WaltNothingRecorded = 'WaltNothingRecorded',
    /** A Console Refresh timed out without the walt reporting in (backend side). */
    WaltRefreshFailed = 'WaltRefreshFailed',
    /** A Console admin sent a restart, power-off or install-update command to a walt. */
    WaltCommandSent = 'WaltCommandSent',
    /** Reports whether the final "device status" packet made it out before Walt powered off. */
    WaltShutdownStatusReported = 'WaltShutdownStatusReported',

    // --- Firmware ---
    /** Walt firmware update failed. */
    FirmwareUpdateError = 'FirmwareUpdateError',
    /** Walt firmware update completed successfully. */
    FirmwareUpdateSuccess = 'FirmwareUpdateSuccess',

    // --- AI ---
    /** AI voice assistant produced a response. */
    AIVoiceAssistantResponse = 'AIVoiceAssistantResponse',
    /** AI voice assistant encountered an error generating a response. */
    AIVoiceAssistantError = 'AIVoiceAssistantResponseError',

    // --- Offline mode ---
    /** Summary of a completed offline-messaging session. */
    OfflineModeSession = 'OfflineModeSession',
    /** Offline mode was requested for a facility. */
    OfflineModeRequested = 'OfflineModeRequested',
    /** Offline mode was cleared for a facility. */
    OfflineModeCleared = 'OfflineModeCleared',

    // --- Peripherals ---
    /** A USB-C peripheral disconnected. */
    UsbcDeviceDisconnected = 'UsbcDeviceDisconnected',
    /** A USB-C peripheral reconnected after a disconnect. */
    UsbcDeviceReconnected = 'UsbcDeviceReconnected',
    /** Short I-key press detected on Valor speaker-mic. */
    ShortValorIKeyUp = 'ShortValorIKeyUp',
    /** An NFC tag was scanned but not handled by the app. */
    NfcUnhandledRead = 'NfcUnhandledRead',
    /** An NFC write was attempted but not handled by the app. */
    NfcUnhandledWrite = 'NfcUnhandledWrite',
    /** An unrecognised HID (USB input) signal was received. */
    UnexpectedHidSignal = 'UnexpectedHidSignal',
    /** Hatch speaker-mic plugged in or unplugged. */
    SpeakerMicConnected = 'SpeakerMicConnected',
    /** A key was pressed on the Hatch speaker-mic. */
    SpeakerMicKeyEvent = 'SpeakerMicKeyEvent',
    /** The Hatch speaker-mic flashlight state changed. */
    SpeakerMicFlashlightEvent = 'SpeakerMicFlashlightEvent',
    /** The Hatch speaker-mic headphone port state changed. */
    SpeakerMicHeadphoneEvent = 'SpeakerMicHeadphoneEvent',
    /** The Hatch speaker-mic reported an internal update. */
    SpeakerMicUpdate = 'SpeakerMicUpdate',
    /** Device volume changed. */
    VolumeChange = 'VolumeChange',
    /** A media (non-PTT) audio item started playing. */
    MediaPlayback = 'MediaPlayback',
    /** Outcome for a photo or video held by a walt's offline media queue. */
    OfflineMediaQueue = 'OfflineMediaQueue',

    // --- Mesh ---
    /** Lifecycle stage marker for mesh networking events. */
    MeshTimeline = 'MeshTimeline',
    /** Result of a mesh network connectivity test. */
    MeshConnectivityTest = 'MeshConnectivityTest',
    /** Periodic count of connected mesh peers (LoRa and DECT). */
    MeshPeersCount = 'MeshPeersCount',
    /** Server->mesh backfill run: how much a facility's returning gateways were caught up on. */
    MeshBackfill = 'MeshBackfill',
    /** A returning gateway resolved no facilities, so no backfill was sent at all. */
    MeshBackfillSkipped = 'MeshBackfillSkipped',
    /** Store-and-forward outbox finished replaying buffered messages after reconnecting to the mesh. */
    MeshOutboxFlushed = 'MeshOutboxFlushed',
    /** Periodic (60s) snapshot of mesh radio window/slot occupancy and TX health, aggregated natively. */
    MeshRadioStats = 'MeshRadioStats',
    /** Mesh radios (DECT + LoRa) were powered on or off, e.g. dock/undock power management. */
    MeshRadioPowerChanged = 'MeshRadioPowerChanged',
    /** Periodic (60s) on-walt counters of mesh-to-cloud forwarding outcomes: connected peers stamped or missing, elected-but-offline drops, forwards performed. */
    MeshForwardStats = 'MeshForwardStats',
    /** Backend could not forward a PTT into the mesh: no connected forwarder was found, or some mesh recipients were unreachable through every chosen forwarder. */
    MeshForwardFailure = 'MeshForwardFailure',
    /** The walt user's weMesh on/off choice changed, from the settings switch or the reset at logout. */
    MeshUserToggleChanged = 'MeshUserToggleChanged',

    // --- Safety ---
    /** Beacon visibility report for a raised on-site SOS: counts, freshness and nearest distance — never a beacon identity. */
    SosBeaconScan = 'SosBeaconScan',
    /** User triggered or cleared a panic / SOS alert. */
    SosEvent = 'SosEvent',
    /** The mesh copy of an SOS failed entirely or skipped facilities with no mesh id. */
    SosMeshSendDegraded = 'SosMeshSendDegraded',
    /** A beacon-carrying SOS was deduped against a same-second PanicReceived without one, discarding the proximity beacon. */
    SosProximityBeaconDiscarded = 'SosProximityBeaconDiscarded',
    /** Emitted for every SOS/panic request the backend processes, per delivered request copy before dedupe — mesh simulcast means several per SOS. */
    SosReceived = 'SosReceived',
    /** A typed SOS was deduped against an untyped same-second PanicReceived, discarding its trigger type. */
    SosTriggerTypeDiscarded = 'SosTriggerTypeDiscarded',
    /** Undefined user detected at a point where a user should be set; aids auth-state debugging. */
    UndefinedUser = 'UndefinedUser',

    // --- Walt hardware metrics ---
    /** Periodic battery current-draw summary on Walt (sampled at 2 Hz). */
    BatteryCurrent = 'BatteryCurrent',
    /** Periodic system-wide CPU utilisation snapshot on Walt. */
    CpuUse = 'CpuUse',
    /** Periodic memory snapshot on Walt. */
    MemoryUse = 'MemoryUse',
    /** Periodic snapshot of Walt's data partition and Couchbase volume space. */
    StorageUse = 'StorageUse',
    /** Exit-monitoring module reported a device exit event. */
    ExitMonitoring = 'ExitMonitoring',

    // --- Change stream ---
    /** A MongoDB change-stream document was dropped before processing. */
    ChangeStreamDiscarded = 'ChangeStreamDiscarded',
    /** A MongoDB change-stream document was received and queued. */
    ChangeStreamEvent = 'ChangeStreamEvent',
    /** A change-stream document was exported to a downstream sink. */
    ChangeStreamExport = 'ChangeStreamExport',
    /** The change-stream consumer queue depth exceeded a threshold. */
    ChangeStreamQueuePressure = 'ChangeStreamQueuePressure',
    /** A change-stream document was transformed. */
    ChangeStreamTransform = 'ChangeStreamTransform',
    /** The change-stream consumer applied back-pressure to the source. */
    ChangeStreamBackPressure = 'ChangeStreamBackPressure',
    /** A large change-stream document was offloaded to Azure Blob Storage. */
    ChangeDocumentStoredInAzureBlob = 'ChangeDocumentStoredInAzureBlob',
    /** A change record was skipped because its account had been deleted; writing it would recreate the dropped database. */
    ChangeRecordSkipped = 'ChangeRecordSkipped',
    /** A change-stream consumer failed on an event whose account has since been deleted, so the event was dropped instead of retried. */
    ChangeEventSkipped = 'ChangeEventSkipped',

    // --- Telemetry stream / consumer infrastructure ---
    /** Per-event-type throughput counter emitted by change-stream consumers. */
    EventMetrics = 'EventMetrics',
    /** Telemetry-stream consumer queue depth exceeded a threshold. */
    TelemetryQueueBackPressure = 'TelemetryQueueBackPressure',
    /** A consumer operation exhausted all retry attempts. */
    RetryLimitExceeded = 'RetryLimitExceeded',
    /** An ingestion parquet writer stored NULL because a timestamp value did not parse. */
    ParquetTimestampUnparseable = 'ParquetTimestampUnparseable',
    /** An ingestion parquet writer stored NULL because a value did not match its column's declared DOUBLE or BOOLEAN type. */
    ParquetValueMismatched = 'ParquetValueMismatched',
    /** One account-month of badgeUpdates was archived to blob, appended to, mismatched, purged for retention, or failed. */
    BadgeUpdatesArchiveMonth = 'BadgeUpdatesArchiveMonth',
    /** A badgeUpdates archive run finished, failed, or found another run holding the archive. */
    BadgeUpdatesArchiveRun = 'BadgeUpdatesArchiveRun',
    /** The badgeUpdates archive dropped an archived month from Mongo, recorded an earlier drop, put one back, held, stranded or failed on one. */
    BadgeUpdatesArchiveMonthDropped = 'BadgeUpdatesArchiveMonthDropped',
    /** A badge history request (DVR playback or person location history) was served from Mongo, the parquet archive, or both. */
    BadgeHistoryQuery = 'BadgeHistoryQuery',
    /** A SQL report or data source named a badgeUpdates month that is no longer in Mongo; reports read Mongo only, so it saw no rows. */
    BadgeUpdatesArchivedMonthQueried = 'BadgeUpdatesArchivedMonthQueried',

    // --- Service lifecycle ---
    /** A backend service started up. */
    StartUp = 'Startup',
    /** A backend service shut down. */
    Shutdown = 'Shutdown',

    // --- Data retention functions ---
    /** Azure data-retention function deleted alert records. */
    AlertDeletion = 'AlertDeletion',
    /** Azure data-retention function deleted channel messages. */
    ChannelDeletion = 'ChannelDeletion',
    /** Azure data-retention function retrieved channels for processing. */
    ChannelRetrieval = 'ChannelRetrieval',
    /** Azure data-retention function deleted event records. */
    EventDeletion = 'EventDeletion',
    /** Azure data-retention function retrieved retention policies. */
    PolicyRetrieval = 'PolicyRetrieval',

    // --- Geofence / facility presence ---
    /** A person entered a geofence zone. */
    GeofenceEnter = 'GeofenceEnter',
    /** A person exited a geofence zone. */
    GeofenceExit = 'GeofenceExit',
    /** A person entered a facility (site). */
    FacilityEnter = 'FacilityEnter',
    /** A person exited a facility (site). */
    FacilityExit = 'FacilityExit',
    /** The phone left tracking circles the OS would refuse out of its geofence registration, instead of crashing on them. */
    TrackingCirclesRejected = 'TrackingCirclesRejected',

    // --- Facility alerts ---
    /** Delivering a facility alert to one recipient failed, without affecting delivery to any other recipient. */
    FacilityAlertPersonDeliveryFailed = 'FacilityAlertPersonDeliveryFailed',
    /** Creating a facility alert failed - e.g. a derived-id collision on `facilityAlerts.insert`. No recipient was notified. */
    FacilityAlertCreateFailed = 'FacilityAlertCreateFailed',
    /** Forwarding a facility alert to one language group's mesh recipients failed. Direct delivery is unaffected. */
    FacilityAlertMeshForwardFailed = 'FacilityAlertMeshForwardFailed',
}
